import { Box, FormControl, Typography } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useEntity } from '../../contexts/EntityContext';
import type { EntityBase } from '@opengov/psp-contract';
import { TestIds } from '../../constants';

// Custom filter that searches by friendlyId, name, and displayName
const entityFilter = createFilterOptions<EntityBase>({
  stringify: (option) => {
    // Combine all searchable fields into a single string for filtering
    return `${option.friendlyId} ${option.name} ${option.displayName}`.toLowerCase();
  },
});

export function EntitySelector() {
  const { activeEntity, entitledEntities, setActiveEntityByFriendlyId } = useEntity();

  return (
    <Box sx={{ px: 2, pt: 2, pb: 2, position: 'relative', zIndex: 1 }}>
      <FormControl fullWidth size="small">
        <Typography
          id="entity-selector-label"
          variant="caption"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'text.secondary',
            mb: 0.5,
            display: 'block',
          }}
        >
          Entity
        </Typography>
        <Autocomplete
          data-testid={TestIds.ENTITY_SELECTOR}
          size="small"
          options={entitledEntities}
          value={activeEntity}
          onChange={(_, value) => {
            if (value) setActiveEntityByFriendlyId(value.friendlyId);
          }}
          getOptionLabel={(option) => option.displayName || option.name || option.friendlyId}
          isOptionEqualToValue={(o, v) => o?.id === v?.id}
          filterOptions={(opts, state) => {
            const filtered = entityFilter(opts, state);
            return filtered;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select entity"
              inputProps={{
                ...params.inputProps,
                'aria-labelledby': 'entity-selector-label',
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.displayName || option.name || option.friendlyId}
            </li>
          )}
          slotProps={{
            listbox: {
              style: {
                maxHeight: '400px',
              },
            },
            popper: {
              placement: 'bottom-start',
              disablePortal: false,
              style: {
                zIndex: 1400, // Higher than Drawer's z-index (1200)
              },
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 4],
                  },
                },
                {
                  name: 'preventOverflow',
                  enabled: true,
                },
              ],
            },
          }}
        />
      </FormControl>
    </Box>
  );
}
